import { SchedulesFilters } from "./components/SchedulesFilters";
import { Box } from "@mui/material";
import { SchedulesCalendar } from "./components/SchedulesCalendar";
import { ScheduleWeekTotal } from "./components/schedule-week-totals/ScheduleWeekTotal";
import {
    useFilteredSchedules,
    useStaffAvailability,
    getFilteredResources,
    consolidateEventsForMonthView,
} from "./utils/scheduleUtils";
import { useStore } from "../../store/store";
import { useDefaultFilters } from "../../components/calendar/calendarUtils";
import { useUser } from "../../api/useAuth";
import { filterUsersRoles } from "../../utils/filterUsersRoles";
import { useUsers } from "../../api/useUsers";
import { useEffect, useState } from "react";
import { CustomEventType } from "./utils/types";
import { useIsMobile } from "../../utils/useIsMobile";
import { useWindowWidth } from "../../utils/useWindowWith";
import { CalendarLoader } from "../../components/calendar/CalendarLoader";
import { useRecords } from "../../api/useRecords";
import { isRoleAdmin, isRoleDirector, isRoleMarketingManager } from "../../utils/rolesUtils";

export const Schedules = () => {
    const { data: user } = useUser();
    const { data: users, status: usersStatus } = useUsers("*.*", filterUsersRoles, "first_name");
    
    const isAdmin = isRoleAdmin(user?.role?.id)
    const isDirector = isRoleDirector(user?.role?.id)
    const isMarketingManager = isRoleMarketingManager(user?.role?.id)
    
    const isAdminDirectorOrMarketing = isAdmin || isDirector || isMarketingManager

    const defaultFilters = useDefaultFilters({ clinic_id: undefined });
    const {
        schedules: { calendarFilters, setCalendarFilters, resourceIds, calendarView, clinicIds },
    } = useStore();

    const [selectMode, setSelectMode] = useState<boolean>(false);
    const [selectedEvents, setSelectedEvents] = useState<Array<CustomEventType>>([]);
    const { data, status } = useRecords("rooms", "*.*", { extraOptions: { sort: "id" } });
    const { data: clinicsData, status: clinicsStatus } = useRecords("clinics", "*.*", { staleTime: Infinity });
    const allResources = data?.items;
    const arrangedResources = getFilteredResources(allResources, resourceIds);

    useEffect(() => {
        if (calendarFilters === null) {
            setCalendarFilters(defaultFilters);
        }
        else {  
            const clinicIdsToSet = clinicIds.length > 0 ? clinicIds : undefined;
            const shouldUpdate = JSON.stringify(calendarFilters.clinic_id) !== JSON.stringify(clinicIdsToSet);
            if (shouldUpdate) {
                setCalendarFilters({ ...calendarFilters, clinic_id: clinicIdsToSet });
            }
        }
    }, [clinicIds, calendarFilters, setCalendarFilters, defaultFilters]);

    const { events, isLoading: staffLoading, isError } = useStaffAvailability(calendarFilters || defaultFilters, clinicsData?.items);
    const isMobile = useIsMobile();
    const windowWith = useWindowWidth();

    const filteredEvents = useFilteredSchedules(events);

    const isLoading = status === "loading" || staffLoading || usersStatus === "loading" || clinicsStatus === "loading";
    if (isError || usersStatus === "error" || clinicsStatus === "error") return <div>error...</div>;

    return (
        <Box style={{ width: isMobile ? windowWith - 16 : windowWith - 280, backgroundColor: "#fff" }}>
            <SchedulesFilters users={users} resources={allResources} clinics={clinicsData?.items} />
            {isLoading ? <CalendarLoader /> : null}
            <SchedulesCalendar
                filteredEvents={calendarView === "month" ? consolidateEventsForMonthView(filteredEvents) : filteredEvents}
                setSelectedEvents={setSelectedEvents}
                selectMode={selectMode}
                setSelectMode={setSelectMode}
                selectedEvents={selectedEvents}
                arrangedResources={arrangedResources}
                users={users}
                clinics={clinicsData?.items}
            />
            {isAdminDirectorOrMarketing && (
                <ScheduleWeekTotal events={filteredEvents} selectMode={selectMode} setSelectMode={setSelectMode} />
            )}
        </Box>
    );
};
